<template>
  <div class="flex h-screen bg-gray-50">
    <SideBar class="flex-shrink-0" />
    <div class="container mx-auto px-4 lg:px-8">
      <div class="mb-8 mt-5 flex justify-between items-center">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Shipping Labels</h1>
          <p class="text-gray-600 mt-2">Manage and track recent shipping labels</p>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm border mb-8">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h2 class="text-xl font-semibold text-gray-800">Recent Labels</h2>
            
            <!-- Filters -->
            <div class="relative w-full md:w-64">
              <input 
                v-model="searchQuery"
                type="text" 
                placeholder="Search business or tracking..." 
                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon class="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
        
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Name</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tracking Number</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Refunded</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="filteredLabels.length === 0">
                <td colspan="5" class="px-6 py-8 text-center text-gray-500">
                  No shipping labels found matching your search.
                </td>
              </tr>
              <tr v-for="label in filteredLabels" :key="label.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ label.businessName }}</td>
                <td class="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" :title="label.address">{{ label.address }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                  <a 
                    :href="getTrackingUrl(label)" 
                    target="_blank" 
                    class="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                    title="Track Package"
                  >
                    {{ label.trackingNumber }}
                    <ExternalLinkIcon class="w-3 h-3" />
                  </a>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                    :class="{
                      'bg-red-100 text-red-800': label.status === 'refunded',
                      'bg-gray-100 text-gray-800': label.status !== 'refunded'
                    }"
                  >
                    {{ label.status === 'refunded' ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex items-center gap-3">
                    <button 
                      @click="printLabel(label)"
                      class="text-gray-600 hover:text-gray-900 flex items-center gap-1"
                      title="Print Label"
                    >
                      <PrinterIcon class="w-4 h-4" />
                      Print
                    </button>
                    <button 
                      v-if="label.status !== 'refunded'"
                      @click="refundLabel(label)"
                      class="text-red-600 hover:text-red-900 flex items-center gap-1"
                      title="Refund Label"
                    >
                      <RotateCcwIcon class="w-4 h-4" />
                      Refund
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import SideBar from '@/components/SideBar.vue'
import { SearchIcon, PrinterIcon, RotateCcwIcon, ExternalLinkIcon } from 'lucide-vue-next'
import Swal from 'sweetalert2'
import { fetchLabels, voidLabel } from '@/api/labels'

// Data
const labels = ref([])

onMounted(async () => {
  try {
    const data = await fetchLabels()
    labels.value = data.labels.map(l => ({
      id: l.id,
      businessName: l.recipient.name ,
      address: formatAddress(l.recipient.address),
      trackingNumber: l.trackingNumber,
      status: l.voided ? 'refunded' : 'created',
      carrier: (l.carrierCode || '').toUpperCase(),
      labelPdfUrl: l.labelPdfUrl
    }))
  } catch (err) {
    console.error('Failed to fetch labels:', err)
    Swal.fire({ icon: 'error', text: 'Failed to load shipping labels' })
  }
})

function formatAddress(addr) {
  if (!addr) return ''
  const parts = [addr.line1, addr.city, addr.state, addr.zip].filter(Boolean)
  return parts.join(', ')
}

const searchQuery = ref('')

// Computed
const filteredLabels = computed(() => {
  if (!searchQuery.value) return labels.value
  
  const query = searchQuery.value.toLowerCase()
  return labels.value.filter(label => 
    label.businessName.toLowerCase().includes(query) || 
    label.trackingNumber.toLowerCase().includes(query) ||
    label.address.toLowerCase().includes(query)
  )
})

// Methods
const getTrackingUrl = (label) => {
  const num = label.trackingNumber
  switch (label.carrier) {
    case 'UPS':
      return `https://www.ups.com/track?tracknum=${num}`
    case 'FedEx':
      return `https://www.fedex.com/fedextrack/?trknbr=${num}`
    case 'USPS':
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${num}`
    default:
      // Fallback or auto-detect based on pattern if carrier not specified
      if (num.startsWith('1Z')) return `https://www.ups.com/track?tracknum=${num}`
      return '#'
  }
}

// Methods


const printLabel = (label) => {
  if (label.labelPdfUrl) {
    window.open(label.labelPdfUrl, '_blank')
  } else {
    Swal.fire({ icon: 'error', text: 'No label URL available' })
  }
}

const refundLabel = async (label) => {
  const result = await Swal.fire({
    title: 'Refund Label?',
    text: `Are you sure you want to refund the label for ${label.trackingNumber}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Yes, refund it'
  })

  if (result.isConfirmed) {
    try {
      await voidLabel(label.id)
      label.status = 'refunded'
      
      await Swal.fire(
        'Refunded!',
        'The shipping label has been refunded.',
        'success'
      )
    } catch (err) {
      console.error('Failed to void label:', err)
      Swal.fire({
        icon: 'error',
        title: 'Refund Failed',
        text: err.response?.data?.error || 'Failed to refund label'
      })
    }
  }
}
</script>
